/**
 * Product analytics (PostHog), app only. The marketing site keeps Clarity.
 *
 * Off unless VITE_POSTHOG_KEY is set, so local dev and previews send nothing.
 * Session replay masks every input and anything marked data-ph-mask: customers'
 * clients' names, addresses and prices live in this app. Revenue and retention
 * numbers stay in our own tables; this is for watching real sessions and for
 * funnels the team can build without SQL.
 */
import type { PostHog } from 'posthog-js';
import { analyticsPrivacyConfig, scrubAnalyticsPayload } from './analyticsPrivacy';

const KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || 'https://us.i.posthog.com';

let client: PostHog | null = null;
let loading: Promise<PostHog | null> | null = null;

export function analyticsEnabled(): boolean {
  return !!KEY && import.meta.env.MODE !== 'test';
}

/** Lazy: the SDK is ~60 KB and never needed to paint the first screen. */
export function initAnalytics(): Promise<PostHog | null> {
  if (!analyticsEnabled()) return Promise.resolve(null);
  if (loading) return loading;
  loading = import('posthog-js').then(({ default: posthog }) => {
    posthog.init(KEY!, {
      ...analyticsPrivacyConfig,
      api_host: HOST,
      capture_pageview: 'history_change',
      capture_pageleave: true,
      // Autocapture is the point: every click, form submit and route change,
      // plus heatmaps, rage clicks and dead clicks. The named events below add
      // the outcomes autocapture cannot see (a booking, a cancellation).
      autocapture: true,
      enable_heatmaps: true,
      rageclick: true,
      capture_dead_clicks: true,
      // Unhandled errors and promise rejections become $exception events, so a
      // customer's "the app is buggy" can be matched to the replay that shows
      // it (ticket f0b76d51: PostHog had no exception data and Sentry had none
      // tied to the user). Console errors stay off: Sentry's console
      // integration already carries them, and this keeps the volume honest.
      // Payloads still pass through before_send's auth-parameter scrubbing.
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        capture_console_errors: false,
      },
      person_profiles: 'identified_only',
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '[data-ph-mask]',
        blockSelector: '[data-ph-block]',
        // Replay URLs/network metadata have their own path before compression.
        maskCapturedNetworkRequestFn: (request) => scrubAnalyticsPayload(request),
      },
      // Staff and support sessions are ours, not customers'.
      loaded: (ph) => { if (pendingOptOut) ph.opt_out_capturing(); },
    });
    client = posthog;
    return posthog;
  }).catch(() => null);
  return loading;
}

let pendingOptOut = false;

export type AnalyticsIdentity = {
  userId: string;
  email?: string | null;
  role?: string | null;
  organizationId?: string | null;
};

/** Called once the role is known. Platform staff sessions are excluded entirely. */
export function identifyUser(who: AnalyticsIdentity): void {
  if (!analyticsEnabled()) return;
  const staff = who.role === 'super_admin' || who.role === 'support_staff';
  void initAnalytics().then((ph) => {
    if (!ph) return;
    if (staff) { pendingOptOut = true; ph.opt_out_capturing(); return; }
    pendingOptOut = false;
    ph.opt_in_capturing();
    ph.identify(who.userId, { role: who.role ?? undefined, organization_id: who.organizationId ?? undefined });
    if (who.organizationId) ph.group('organization', who.organizationId);
  });
}

/** Plan facts on the person and the organization, once the subscription query resolves. */
export function setPlanProperties(p: { organizationId?: string | null; planKey: string | null; status: string | null; billingCycle?: string | null }): void {
  if (!analyticsEnabled()) return;
  void initAnalytics().then((ph) => {
    if (!ph) return;
    const props = { plan_key: p.planKey ?? 'none', subscription_status: p.status ?? 'none', billing_cycle: p.billingCycle ?? undefined };
    ph.setPersonProperties(props);
    if (p.organizationId) ph.group('organization', p.organizationId, props);
  });
}

export function resetAnalytics(): void {
  if (!analyticsEnabled()) return;
  client?.reset();
}

/** Named product events. Keep the list small and the names stable. */
export type AnalyticsEvent =
  | 'trial_offer_viewed'
  | 'trial_offer_clicked'
  | 'signup_completed'
  | 'wizard_step'
  | 'wizard_started'
  | 'wizard_resumed'
  | 'wizard_resume_clicked'
  | 'wizard_dismissed'
  | 'wizard_done_viewed'
  // A wizard write that did not land (#1657). Wizard failures show a
  // toast.warning (or the session banner), which error_shown never counts.
  | 'wizard_save_failed'
  | 'wizard_session_recovered'
  | 'wizard_session_recovery_failed'
  | 'demo_booked'
  // Dashboard onboarding checklist and first-run product tours.
  | 'onboarding_item_action'
  | 'onboarding_item_toggled'
  | 'onboarding_item_dismissed'
  | 'onboarding_checklist_hidden'
  | 'tour_started'
  | 'tour_completed'
  | 'tour_skipped'
  // Import (concierge) funnel. Counts and flags only, never what the customer wrote.
  | 'import_offer_viewed'
  | 'import_offer_clicked'
  | 'import_offer_dismissed'
  | 'import_explore_later_clicked'
  | 'support_card_clicked'
  | 'import_request_started'
  | 'import_files_uploaded'
  | 'import_lumen_started'
  | 'import_lumen_finished'
  | 'import_lumen_unavailable'
  | 'import_request_submitted'
  | 'import_request_cancelled'
  | 'import_plan_review_opened'
  | 'import_plan_approved'
  | 'import_changes_requested'
  | 'import_completed'
  | 'checkout_started'
  | 'plan_changed'
  | 'cancel_flow_opened'
  | 'cancel_reason_chosen'
  | 'cancel_offer_accepted'
  | 'cancel_offer_declined'
  | 'plan_cancelled'
  | 'plan_reactivated'
  | 'trial_ended'
  | 'lumen_suggestion_event'
  | 'demo_video_event'
  | 'error_shown'
  | 'lead_form_submitted'
  | 'proposal_accepted'
  | 'deposit_payment_started'
  | 'deposit_payment_completed'
  | 'deposit_payment_failed';

export function track(event: AnalyticsEvent, properties?: Record<string, unknown>): void {
  if (!analyticsEnabled()) return;
  void initAnalytics().then((ph) => ph?.capture(event, properties));
}

const DEVICE_ID_KEY = 'lm_device_id';

/**
 * The id the duplicate-trial hold matches signups on (2026-09-23): PostHog's
 * distinct id when analytics is loaded (the same id its sessions carry), else
 * a per-browser id kept in localStorage so the check still works with
 * analytics off. A browser that blocks storage yields a fresh id per load,
 * which only ever means "no match".
 */
export function getDeviceId(): string {
  const fromPostHog = client?.get_distinct_id?.();
  if (typeof fromPostHog === 'string' && fromPostHog.length > 0) return fromPostHog;
  try {
    const existing = window.localStorage.getItem(DEVICE_ID_KEY);
    if (existing) return existing;
    const fresh = `lm_${crypto.randomUUID()}`;
    window.localStorage.setItem(DEVICE_ID_KEY, fresh);
    return fresh;
  } catch {
    return `lm_${crypto.randomUUID()}`;
  }
}
